/**
 * Post-Quote Authentication Page
 * 
 * This page is shown after a homeowner submits a quote request.
 * It allows them to create an account or log in to track their quote.
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, Mail, Lock, User, Eye, EyeOff, CheckCircle, 
  ArrowRight, FileText, MessageSquare, Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function PostQuoteAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, register, googleLogin, token } = useAuth();
  
  // Get quote data from location state
  const quoteData = location.state?.quoteData;
  const pendingQuoteId = location.state?.pendingQuoteId;
  const submittedEmail = location.state?.email || quoteData?.homeowner_email || "";
  
  const [mode, setMode] = useState("create"); // "create" or "login"
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: quoteData?.homeowner_name || "",
    email: submittedEmail,
    password: "",
    confirmPassword: ""
  });
  
  // If user is already logged in, link the quote and redirect
  useEffect(() => {
    if (user && pendingQuoteId) {
      linkQuoteToUser();
    } else if (user) {
      navigate("/dashboard", { state: { tab: "quotes" } });
    }
  }, [user]);
  
  // Redirect if no quote data
  useEffect(() => {
    if (!quoteData && !pendingQuoteId) {
      navigate("/request");
    }
  }, [quoteData, pendingQuoteId]);
  
  const linkQuoteToUser = async () => {
    if (!pendingQuoteId || !token) return;
    
    try {
      await axios.post(`${API}/quotes/${pendingQuoteId}/link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote linked to your account!");
      navigate("/dashboard", { state: { tab: "quotes" } });
    } catch (error) {
      navigate("/dashboard", { state: { tab: "quotes" } });
    }
  };
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      // Register the user
      const newUser = await register({
        email: formData.email,
        password: formData.password,
        name: formData.name || formData.email.split("@")[0],
        role: "homeowner"
      });
      
      toast.success("Account created successfully!");
      
      // The quote linking will happen via the useEffect when user state updates
      // If we have a pending quote, link it
      if (pendingQuoteId) {
        // Wait for token to be available then link
        setTimeout(() => {
          navigate("/dashboard", { state: { tab: "quotes", linkQuoteId: pendingQuoteId } });
        }, 500);
      } else {
        navigate("/dashboard", { state: { tab: "quotes" } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please enter your email and password");
      return;
    }
    
    setLoading(true);
    
    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back!");
      
      // Navigate to dashboard with quotes tab
      if (pendingQuoteId) {
        navigate("/dashboard", { state: { tab: "quotes", linkQuoteId: pendingQuoteId } });
      } else {
        navigate("/dashboard", { state: { tab: "quotes" } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    // Store pending quote ID in session for after OAuth redirect
    if (pendingQuoteId) {
      sessionStorage.setItem("pendingQuoteLink", pendingQuoteId);
    }
    googleLogin();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-testid="post-quote-auth">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933]">Contractor Hub LLC</span>
          </Link>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8 sm:py-12">
        {/* Success Message */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-manrope font-bold text-2xl text-[#1F2933] mb-2">
            Your Quote Has Been Submitted!
          </h1>
          <p className="text-[#1F2933]/70">
            {quoteData?.categoryName || "Your project"} estimate: {" "}
            <span className="font-semibold text-[#0FA3B1]">
              ${quoteData?.estimatedPrice?.low?.toLocaleString() || "0"} – ${quoteData?.estimatedPrice?.high?.toLocaleString() || "0"}
            </span>
          </p>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <h2 className="font-semibold text-[#1F2933] mb-4 text-center">
            Create an account to:
          </h2>
          <div className="space-y-3">
            {[
              { icon: FileText, text: "Track your quote status in real-time" },
              { icon: MessageSquare, text: "Receive and compare contractor responses" },
              { icon: Shield, text: "Manage all your home projects in one place" }
            ].map((benefit) => (
              <div key={benefit.text} className="flex items-center gap-3 text-[#1F2933]/80">
                <div className="w-8 h-8 rounded-lg bg-[#0FA3B1]/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-4 h-4 text-[#0FA3B1]" />
                </div>
                <span className="text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Auth Form */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setMode("create")}
              className={`flex-1 py-4 text-sm font-medium transition-colors ${
                mode === "create"
                  ? "text-[#0FA3B1] border-b-2 border-[#0FA3B1] bg-[#0FA3B1]/5"
                  : "text-[#1F2933]/60 hover:text-[#1F2933]"
              }`}
              data-testid="create-account-tab"
            >
              Create Account
            </button>
            <button
              onClick={() => setMode("login")}
              className={`flex-1 py-4 text-sm font-medium transition-colors ${
                mode === "login"
                  ? "text-[#0FA3B1] border-b-2 border-[#0FA3B1] bg-[#0FA3B1]/5"
                  : "text-[#1F2933]/60 hover:text-[#1F2933]"
              }`}
              data-testid="login-tab"
            >
              Sign In
            </button>
          </div>

          <div className="p-6">
            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full py-5 rounded-xl border-gray-200 mb-4"
              onClick={handleGoogleLogin}
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#1F2933]/50">or</span>
              </div>
            </div>

            {mode === "create" ? (
              /* Create Account Form */
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-[#1F2933]/70 text-sm">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2933]/40" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="pl-10 py-5 rounded-xl border-gray-200"
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#1F2933]/70 text-sm">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2933]/40" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="pl-10 py-5 rounded-xl border-gray-200"
                      required
                      data-testid="email-input"
                    />
                  </div>
                  {submittedEmail && (
                    <p className="text-xs text-[#0FA3B1] mt-1">Pre-filled from your quote</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#1F2933]/70 text-sm">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2933]/40" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      className="pl-10 pr-10 py-5 rounded-xl border-gray-200"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1F2933]/40 hover:text-[#1F2933]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-[#1F2933]/70 text-sm">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2933]/40" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      className="pl-10 py-5 rounded-xl border-gray-200"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 rounded-xl bg-[#0FA3B1] hover:bg-[#0D8C98] text-white font-medium"
                  data-testid="create-account-btn"
                >
                  {loading ? "Creating Account..." : "Create Account & View Quote"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            ) : (
              /* Login Form */
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-[#1F2933]/70 text-sm">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2933]/40" />
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="pl-10 py-5 rounded-xl border-gray-200"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="login-password" className="text-[#1F2933]/70 text-sm">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2933]/40" />
                    <Input
                      id="login-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="pl-10 pr-10 py-5 rounded-xl border-gray-200"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1F2933]/40 hover:text-[#1F2933]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 rounded-xl bg-[#0FA3B1] hover:bg-[#0D8C98] text-white font-medium"
                  data-testid="login-btn"
                >
                  {loading ? "Signing In..." : "Sign In & View Quotes"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Skip Option */}
        <div className="text-center mt-6">
          <p className="text-sm text-[#1F2933]/60 mb-2">
            Want to create an account later?
          </p>
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-[#1F2933]/70 hover:text-[#1F2933]"
            data-testid="skip-btn"
          >
            Return to Home
          </Button>
        </div>
      </main>
    </div>
  );
}
